import { checkOrderPool, correlationShopOrder, processed } from '@/apis/callCenter/cloudGroupCall'
import { rxkMessageBox } from '@/components/common/RxkMessageBox'
import { useResource } from '../useResource'
import { customerBtnCode, seaBtnCode } from '@/utils/preObjectManage/utils'
import { useCallCenter } from '@/stores/modules/callCenter'
import { callCenterStatusEnum, dialingScenes, seatStatusEnum } from '@/enums/callCenter'
import { callCenterLinkInfo } from '@/apis/callCenter/callCenter'
import { seaReceive } from '@/apis/highSeaManage'
import { useCall } from './call'
import { restoreRecycleBin } from '@/apis/recycleBin'
import { useAccountStore } from '@/stores/modules/account'
import { customerBatchDistributeProgress } from '@/apis/customerManage'
import MyGlobalEvent from '@/utils/bus/baseBus'

/**
 * 群呼
 */
export function useGroupCall() {
  const { getMenuInfo } = useResource()
  const { userInfo } = useAccountStore()
  const callCenter = useCallCenter()
  const useCallHooks = useCall()
  async function checkOrderPoolFn(id: string, form?: string) {
    const isLoading = ElLoading.service({
      lock: true
    })
    const res = await checkOrderPool({ id: id })
    if (res.hitHighSeaPool) {
      // 命中公海领取
      rxkMessageBox('该数据在公海中已存在，请先领取客户', '提示', {
        confirmButtonText: '领取',
        showCancelButton: true,
        type: 'warning'
      })
        .then(() => {
          isLoading.close()
          // 执行领取
          callCenter.setBizId(res.bizId)
          receive(
            {
              relateShopOrderId: res.hitHighSeaOrderId,
              belongOpenSeaId: res.belongOpenSeaId,
              id
            },
            2
          )
        })
        .catch(() => {
          isLoading.close()
        })
    } else if (res.hitRecyclePool) {
      rxkMessageBox('该数据在回收站中已存在，请先还原客户', '提示', {
        confirmButtonText: '还原',
        showCancelButton: true,
        type: 'warning'
      })
        .then(async () => {
          isLoading.close()
          restoreRecycle({ hitRecycleOrderId: res.hitRecycleOrderId, id })
        })
        .catch(() => {
          isLoading.close()
        })
    } else {
      const menu = await getMenuInfo('CRM', 'customerManage', false)
      isLoading.close()
      if (menu.resourceList && menu.resourceList.length > 0) {
        if (menu.resourceList.find((el: any) => el.code === customerBtnCode.CREATE)) {
          if (callCenter.dialingScenes === dialingScenes.groupCall) {
            return callingCreate({ form, id })
          } else {
            return create(id)
          }
        } else {
          ElMessage.error('请开通新建客户权限')
        }
      }
    }
  }

  /**
   * 群呼新建
   */
  async function create(id: string) {
    const phone = await useCallHooks.queryPlaintextPhoneNumber({
      bizCode: 80,
      dataId: id
    })
    const link = await callCenterLinkInfo({
      callerPhone: phone
    })
    return {
      data: link,
      msg: 'create'
    }
  }

  /**
   * 领取
   */
  function receive(row: Recordable, type?: number) {
    const loading = ElLoading.service()
    getMenuInfo('CRM', 'highSeaManage', false).then(async (res: Recordable) => {
      if (res.resourceList && res.resourceList.length > 0) {
        if (res.resourceList.find((el: any) => el.code === seaBtnCode.RECEIVE)) {
          try {
            const res = await seaReceive({
              trackIds: [row.relateShopOrderId],
              seaId: row.belongOpenSeaId
            })
            loading.close()
            if (res && res.errorMessage) {
              ElMessage.error(res.errorMessage)
            } else {
              // 领取成功，打开详情页
              ElMessage.success('领取成功')
              if (callCenter.dialingScenes === dialingScenes.groupCall) {
                callingReceive(row)
              } else {
                if (type === 1) {
                  await relatepProcessed(row.id)
                  MyGlobalEvent.$emit('cloudGroupCallReload')
                  MyGlobalEvent.$emit('cloudGroupCallWorkConsoleRefresh')
                } else {
                  // 关联saas客单
                  await correlationShopOrderFn({
                    concatId: row.id,
                    linkDataId: row.relateShopOrderId
                  })
                  await relatepProcessed(row.id)
                  MyGlobalEvent.$emit('cloudGroupCallReload')
                  MyGlobalEvent.$emit('cloudGroupCallWorkConsoleRefresh')
                }
              }
            }
          } catch (error) {
            loading.close()
          }
        } else {
          ElMessage.error('领取失败，暂无权限')
          loading.close()
          if (callCenter.dialingScenes === dialingScenes.groupCall) {
            callCenter.reportSeatStatus({
              status: seatStatusEnum.LEAVE,
              viewSeatStatus: callCenter.callCenterStatus
            })
          }
        }
      }
    })
  }

  /**
   * 呼叫中领取
   */
  async function callingReceive(row: Recordable) {
    // 上报状态-整理中
    callCenter.reportSeatStatus({
      status: seatStatusEnum.LEAVE,
      viewSeatStatus: callCenterStatusEnum.CLEANUP
    })
    // 关联saas客单
    await correlationShopOrderFn({
      concatId: row.id,
      linkDataId: row.relateShopOrderId
    })
    useCallHooks.jumpToDetail(row.relateShopOrderId, '客户详情')
  }

  /**
   * 呼叫中新建
   */
  async function callingCreate({ form, id }: { form?: string; id?: string }) {
    // 上报状态-整理中
    callCenter.reportSeatStatus({
      status: seatStatusEnum.LEAVE,
      viewSeatStatus: callCenterStatusEnum.CLEANUP
    })
    if (form === 'relateCustomer' && id) {
      return create(id)
    } else {
      // 判断被叫号码和联系人名称是否加密，其中一个加密就调用解密接口
      let plaintextPhone: string =
          callCenter.groupCallUserInfo?.title?.desensitizePhoneNum || '' || '',
        plaintextName: string = callCenter.groupCallUserInfo?.title?.realName || ''
      if (
        useCallHooks.isEncryption({
          name: plaintextName,
          phone: plaintextPhone
        }) &&
        callCenter.groupCallUserInfo?.title?.customerId
      ) {
        const encryptionInfo: any = await useCallHooks.queryPlaintextInfo({
          bizCode: 80,
          dataId: callCenter.groupCallUserInfo?.title?.customerId
        })
        plaintextPhone = encryptionInfo?.plaintextPhone || plaintextPhone
        plaintextName = encryptionInfo?.plaintextName || plaintextName
      }
      // 设置新建客户姓名和电话
      callCenter.setCallCenterAddCustomData({
        realName: callCenter.groupCallUserInfo?.title?.realName || '',
        originRealName: callCenter.groupCallUserInfo?.title?.realName || '',
        linkPhone: callCenter.groupCallUserInfo?.title?.desensitizePhoneNum || '',
        plaintextPhone,
        plaintextName
      })
      callCenter.setCallAddCustom(true)
      callCenter.setShowCallScreen(false)
    }
  }
  // 关联后回调
  async function relatepProcessed(id: string) {
    return new Promise((resolve, reject) => {
      processed({
        id: id
      })
        .then(res => {
          resolve(res)
        })
        .catch(err => {
          reject(err)
        })
    })
  }

  /**
   * 关联saas客单
   */
  async function correlationShopOrderFn(row: Recordable) {
    await correlationShopOrder({
      linkType: 1,
      concatId: row.concatId,
      linkDataId: row.linkDataId
    })
  }

  /**
   * 回收站还原
   */
  async function restoreRecycle(row: Recordable) {
    const loading = ElLoading.service()
    try {
      const result = await restoreRecycleBin({
        trackIds: [row.hitRecycleOrderId],
        type: 1,
        userIds: [userInfo.id]
      })
      const timer = setInterval(async () => {
        try {
          const progress = await customerBatchDistributeProgress({
            key: result
          })
          if (!progress || progress.isComplete === null) {
            ElMessage.error('还原失败')
            clearInterval(timer)
            loading.close()
          }
          if (String(progress.isComplete) === '1') {
            clearInterval(timer)
            const errorMessageList = progress.errorMessageList
            if (errorMessageList && errorMessageList.length > 0) {
              ElMessage.error(errorMessageList[0])
            } else {
              ElMessage.success('还原成功')
              clearInterval(timer)
              await correlationShopOrderFn({
                concatId: row.id,
                linkDataId: row.hitRecycleOrderId
              })
              await relatepProcessed(row.id)
              MyGlobalEvent.$emit('cloudGroupCallReload')
              MyGlobalEvent.$emit('cloudGroupCallWorkConsoleRefresh')
            }
            loading.close()
          }
        } catch (error) {
          loading.close()
        }
      }, 1000)
    } catch (error) {
      loading.close()
    }
  }
  return {
    checkOrderPoolFn,
    correlationShopOrderFn,
    relatepProcessed,
    receive
  }
}
